import { type ComponentProps } from 'react'
import { twMerge } from 'tailwind-merge'
import { ItemGroupSizeContext, type ItemSize } from './context'

interface ItemGroupProps extends ComponentProps<'div'> {
  /** The density of every Item in the group. An Item's own `size` wins. */
  size?: ItemSize
  /** The hairline between rows. Off for a list the eye already parses, such as rows with media tiles. */
  separator?: boolean
}

// An Item pads itself by size. Anything else in the list, a justified Field, takes the group's padding
// so its edges line up with the Items around it.
const FIELD_PADDING: Record<ItemSize, string> = {
  md: '[&>:not([data-slot=item])]:py-3',
  sm: '[&>:not([data-slot=item])]:py-2',
}

/** A column of rows with a hairline between each pair. Items and justified Fields mix freely. */
function ItemGroup({ className, size = 'md', separator = true, children, ...props }: ItemGroupProps) {
  return (
    <ItemGroupSizeContext.Provider value={size}>
      <div
        data-slot="item-group"
        className={twMerge(
          'flex w-full flex-col',
          // Without a line between them, rows need air instead to read as separate.
          separator ? '[&>*]:border-border [&>*:last-child]:border-b-0 [&>*]:border-b' : 'gap-2',
          FIELD_PADDING[size],
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </ItemGroupSizeContext.Provider>
  )
}

export { ItemGroup }
export type { ItemGroupProps }
